import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  User,
  Building2,
  MapPin,
  RefreshCw,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientProfilePage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  
  // Form states
  const [personalInfo, setPersonalInfo] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    company_name: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    physical_address: ''
  });

  const normalizePhone = (phone) => {
    if (!phone) return '';
    let p = String(phone).trim();
    if (p.endsWith('.0')) p = p.slice(0, -2);
    const digits = p.replace(/[^\d]/g, '');
    if (digits.length === 11 && digits.startsWith('27')) {
      return `+${digits.slice(0,2)} ${digits.slice(2,4)} ${digits.slice(4,7)} ${digits.slice(7)}`;
    }
    if (digits.length === 10 && digits.startsWith('0')) {
      return `+27 ${digits.slice(1,3)} ${digits.slice(3,6)} ${digits.slice(6)}`;
    }
    return digits.length > 9 ? `+${digits}` : p;
  };

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch user details and client profile
      const [userRes, clientRes] = await Promise.all([
        axios.get(`${API_URL}/api/auth/me`, { headers }),
        axios.get(`${API_URL}/api/client/profile`, { headers }).catch(() => ({ data: null }))
      ]);
      
      const userData = userRes.data;
      const clientData = clientRes.data;
      
      setProfileData({ user: userData, client: clientData });
      
      setPersonalInfo({
        first_name: userData?.first_name || '',
        last_name: userData?.last_name || '',
        phone: normalizePhone(userData?.phone || clientData?.contact_phone || ''),
        company_name: clientData?.company_name || '',
        contact_person: clientData?.contact_person || '',
        contact_email: clientData?.contact_email || userData?.email || '',
        contact_phone: normalizePhone(clientData?.contact_phone || ''),
        physical_address: clientData?.physical_address || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Update user details
      await axios.put(`${API_URL}/api/profile/personal`, {
        first_name: personalInfo.first_name,
        last_name: personalInfo.last_name,
        phone: personalInfo.phone
      }, { headers });
      
      toast.success('Profile updated successfully');
      fetchProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="client-profile-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2].map(i => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-48 w-full" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-profile-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#212529] font-['Manrope']">
            My Profile
          </h1>
          <p className="text-[#6C757D] mt-1">
            Manage your personal and company information
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchProfile}
          className="flex items-center gap-2"
          data-testid="refresh-profile-btn"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card data-testid="personal-info-section">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5 text-[#1D2D2A]" />
              Personal Information
            </CardTitle>
            <CardDescription>Your contact details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={personalInfo.first_name}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, first_name: e.target.value }))}
                  data-testid="input-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={personalInfo.last_name}
                  onChange={(e) => setPersonalInfo(prev => ({ ...prev, last_name: e.target.value }))}
                  data-testid="input-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={profileData?.user?.email || ''}
                disabled
                className="bg-gray-50"
              />
              <p className="text-xs text-gray-500">Contact support to change your email address</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={personalInfo.phone}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+27 XX XXX XXXX"
                data-testid="input-phone"
              />
            </div>
          </CardContent>
        </Card>

        {/* Company Information (Read-only) */}
        <Card data-testid="company-info-section">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#1D2D2A]" />
              Company Information
            </CardTitle>
            <CardDescription>Your linked company details (contact admin to update)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={personalInfo.company_name || 'Not set'}
                disabled
                className="bg-gray-50"
              />
            </div>
            <div className="space-y-2">
              <Label>Client ID</Label>
              <Input
                value={user?.client_id || 'Not linked'}
                disabled
                className="bg-gray-50"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact Person</Label>
              <Input
                value={personalInfo.contact_person || 'Not set'}
                disabled
                className="bg-gray-50"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact Email</Label>
              <Input
                value={personalInfo.contact_email || 'Not set'}
                disabled
                className="bg-gray-50"
              />
            </div>
          </CardContent>
        </Card>

        {/* Address Information (Read-only) */}
        <Card data-testid="address-section" className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#1D2D2A]" />
              Address Information
            </CardTitle>
            <CardDescription>Company address (contact admin to update)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>Physical Address</Label>
              <Input
                value={personalInfo.physical_address || 'Not set'}
                disabled
                className="bg-gray-50"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSaveProfile} 
          disabled={saving}
          className="bg-[#1D2D2A] hover:bg-[#243D37]"
          data-testid="save-profile-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
